<template>
  <div class="spinner-wheel-entries">
    <h5 class="spinner-label-setting font-bold">
      {{ $i18nPresenter.t('entries') }}
    </h5>
    <textarea
      ref="entryInput"
      v-model="newEntry"
      name="entries-editor"
      class="entries-editor"
      rows="4"
      :placeholder="$i18nPresenter.t('multipleEntriesPlaceholder')"
    />
    <p class="text-red-100 text-sm h-6 pb-2">
      {{ isReachedMaxLength ? $i18nPresenter.t('entriesMaxLength') : '' }}
    </p>
    <aha-antd-button
      v-aha-emit-action
      name="addSpinnerWheelEntries"
      type="submit"
      variant="secondary"
      class="w-full"
      :submitting="submitting"
      :disabled="isAddButtonDisabled"
      @click="submitNewEntries"
    >
      <span v-if="!submitting">{{ $i18nPresenter.t('Save') }}</span>
    </aha-antd-button>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import Socket from '@/socket'
import SlideOptionApi from '@/api/slideOption'
import EventBus from '@/eventBus'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import SpinnerWheelConfig from '@/constant/SpinnerWheelConfig'

export default {
  data() {
    return {
      newEntry: '',
      submitting: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    isAddButtonDisabled() {
      return !this.newEntry || this.submitting || this.isReachedMaxLength
    },
    slideOptions() {
      return (
        this.slideActive
        && this.slideActive.SlideOptions
        && this.slideActive.SlideOptions.filter((option) => option.id)
      ) || []
    },
    rawEntries() {
      return this.newEntry
        .split(/\r?\n/)
        .filter((item) => item.trim())
    },
    isReachedMaxLength() {
      return this.rawEntries.length > SpinnerWheelConfig.SPINNER_WHEEL_LIMIT
    },
  },
  watch: {
    slideOptions: {
      handler(options) {
        this.initEntry(options)
      },
      immediate: true,
    },
  },
  mounted() {
    this.autoFocusInputEntry()
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    autoFocusInputEntry() {
      this.$nextTick(() => {
        if (!this.slideOptions || !this.slideOptions.length) {
          this.$refs.entryInput.focus()
        }
      })
    },
    async submitNewEntries() {
      if (this.slideActive.wheelStatus === 'spinning') return
      if (!this.newEntry || this.submitting) return

      const entries = this.rawEntries
        .map((entry) => ({ title: entry.trim() }))

      if (entries.length <= this.slideOptions.length) {
        this.deleteAndUpdateEntries(entries, this.slideOptions)
      } else {
        this.addAndUpdateEntries(entries, this.slideOptions)
      }
    },
    initEntry(options) {
      if (options && options.length) {
        this.newEntry = options
          .map((option, index) => {
            const defaultEntryTitle = this.$i18nPresenter.t('entry_number', {
              number: index + 1,
            })
            const optionTitle = option && option.title && option.title.trim()

            return optionTitle || defaultEntryTitle
          })
          .join('\n')
      }
    },
    _preparePayloadToAddAndUpdate(newEntries, oldEntries) {
      let order = 0

      if (oldEntries && oldEntries.length) {
        order = oldEntries.slice(-1)[0].order
      }

      const sharedPayload = {
        accessCode: this.presentation.uniqueAccessCode,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        socketId: Socket.getSocketId(),
      }

      const optionsToUpdate = oldEntries
        .map((entry, index) => {
          const newTitle = newEntries[index].title.trim()

          if (entry.title.trim() !== newTitle) {
            return {
              ...sharedPayload,
              id: entry.id,
              title: newTitle,
              order: entry.order,
            }
          }

          return null
        })
        .filter((entry) => entry !== null)

      const addedEntries = newEntries
        .slice(oldEntries.length)
        .map((entry) => ({
          ...sharedPayload,
          title: entry.title,
          order: ++order,
        }))

      return {
        ...sharedPayload,
        options: addedEntries,
        optionsToUpdate,
        oldEntries,
      }
    },
    async _requestToAddAndUpdate(params) {
      const { oldEntries, ...payload } = params

      this.submitting = true

      try {
        const {
          SlideOptions: slideOptions,
        } = await SlideOptionApi.createSpinnerWheelOptions(payload)
        this.newEntry = ''

        const updatedOptions = [
          ...payload.optionsToUpdate,
          ...slideOptions,
        ]
        const newSlideOptions = [...oldEntries, ...slideOptions]

        updatedOptions.forEach((updatedEntry) => {
          const index = newSlideOptions.findIndex((entry) => entry.id === updatedEntry.id)

          if (index !== -1) {
            newSlideOptions.splice(index, 1, updatedEntry)
          }
        })

        this.updateSlideActiveAction({
          SlideOptions: newSlideOptions,
        })
        EventBus.$emit('createEntries', slideOptions)
        Utils.showToastSuccess(this.$i18nPresenter.t('wheel_updated'))
        this._trackEvent()
      } catch (error) {
        this.$store.dispatch('setSyncPresentation', 3)
        Sentry.captureException(error)
        if (error.body && error.body.message) {
          Utils.showToastError(error.body.message)
        } else {
          Utils.showToastError(this.$i18nPresenter.t('Something went wrong, please check your connection or contact us for support'))
        }
      } finally {
        this.submitting = false
      }
    },
    _preparePayloadToDeleteAndUpdate(newEntries, oldEntries) {
      const sharedPayload = {
        accessCode: this.presentation.uniqueAccessCode,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        socketId: Socket.getSocketId(),
      }

      const newEntriesLength = newEntries.length
      const deletedEntries = oldEntries
        .map((entry, index) => {
          const isDeleted = index >= newEntriesLength

          return {
            ...sharedPayload,
            id: entry.id,
            title: isDeleted ? entry.title : newEntries[index].title,
            order: entry.order,
            deleted: isDeleted,
          }
        })

      return {
        ...sharedPayload,
        optionsToUpdate: deletedEntries,
        deletedEntries,
      }
    },
    async _requestToDeleteAndUpdate(params) {
      const { deletedEntries, ...payload } = params

      this.submitting = true

      try {
        await SlideOptionApi.createSpinnerWheelOptions(payload)
        this.newEntry = ''

        this.updateSlideActiveAction({
          SlideOptions: deletedEntries.filter((entry) => !entry.deleted),
        })
        EventBus.$emit('updateEntries')
        Utils.showToastSuccess(this.$i18nPresenter.t('wheel_updated'))
        this._trackEvent()
      } catch (error) {
        this.$store.dispatch('setSyncPresentation', 3)
        Sentry.captureException(error)
        if (error.body && error.body.message) {
          Utils.showToastError(error.body.message)
        } else {
          Utils.showToastError(this.$i18nPresenter.t('Something went wrong, please check your connection or contact us for support'))
        }
      } finally {
        this.submitting = false
      }
    },
    async addAndUpdateEntries(newEntries, oldEntries) {
      const payload = this._preparePayloadToAddAndUpdate(newEntries, oldEntries)

      await this._requestToAddAndUpdate(payload)
    },
    async deleteAndUpdateEntries(newEntries, oldEntries) {
      const payload = this._preparePayloadToDeleteAndUpdate(newEntries, oldEntries)

      this._requestToDeleteAndUpdate(payload)
    },
    _trackEvent() {
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Spinner Wheel > Content Tab > Input Entry > Edit Entries',
        null,
        null,
        {
          presentationId: this.presentation.id,
          slideId: this.slideActive.id,
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.spinner-wheel-entries {
  padding-top: 16px;
  padding-bottom: 16px;

  .spinner-label-setting {
    margin-bottom: 12px;
    color: #282E3E;
  }

  .entries-editor {
    border: 1px solid var(--secondary-20, #D3D7E1);
    background: #FFF;
    color: var(--base-90, #313131);
    border-radius: 4px;
    width: 100%;
    padding: 8px;

    &::placeholder {
      color: var(--base-70, #999);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 142.857% */
    }

    &:focus {
      border: 1px solid var(--primary-90, #348EF6);
    }
  }
}
</style>
